import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { useLanguage } from '../../context/LanguageContext';

const API = '/api/v1/fsm/settings';

const FSMSettings = () => {
  const { lang } = useLanguage();
  const isRTL = lang === 'ar';
  const T = (ar, en) => isRTL ? ar : en;

  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [testingErp, setTestingErp] = useState(false);
  const [erpTestResult, setErpTestResult] = useState(null);
  const [subsidiaries, setSubsidiaries] = useState([]);
  const [loadingSubs, setLoadingSubs] = useState(false);

  useEffect(() => { fetchSettings(); }, []);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(API, { headers: { Authorization: `Bearer ${token}` } });
      const d = await res.json();
      if (d.success) setData(d.data || {});
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleChange = (field, value) => {
    setData(prev => ({ ...prev, [field]: value }));
    setSaved(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(API, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      const d = await res.json();
      if (d.success) { setSaved(true); setTimeout(() => setSaved(false), 3000); }
      else alert(d.message || 'Error saving');
    } catch (e) { alert('Network error'); }
    finally { setSaving(false); }
  };

  const fetchSubsidiaries = async (dbName) => {
    if (!dbName) return;
    setLoadingSubs(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/v1/fsm/erp-sync/subsidiaries?database=' + encodeURIComponent(dbName), {
        headers: { Authorization: 'Bearer ' + token },
      });
      const d = await res.json();
      if (d.success) setSubsidiaries(d.data || []);
    } catch (e) { console.error(e); }
    finally { setLoadingSubs(false); }
  };

  const testErpConnection = async () => {
    if (!data.erp_database_name) { setErpTestResult({ ok: false, msg: T('ادخل اسم قاعدة البيانات', 'Enter database name') }); return; }
    setTestingErp(true); setErpTestResult(null);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/v1/fsm/settings/test-erp', {
        method: 'POST',
        headers: { Authorization: 'Bearer ' + token, 'Content-Type': 'application/json' },
        body: JSON.stringify({ erp_database_name: data.erp_database_name, erp_api_url: data.erp_api_url }),
      });
      const d = await res.json();
      if (d.success) {
        setErpTestResult({ ok: true, msg: T('الاتصال ناجح', 'Connection successful') + ' (' + (d.total_clients || 0) + ' ' + T('عميل', 'clients') + ')' });
        fetchSubsidiaries(data.erp_database_name);
      } else {
        setErpTestResult({ ok: false, msg: d.message || T('فشل الاتصال', 'Connection failed') });
      }
    } catch (e) { setErpTestResult({ ok: false, msg: T('خطأ في الشبكة', 'Network error') }); }
    finally { setTestingErp(false); }
  };

  // Auto-load subsidiaries if database is already configured
  useEffect(() => {
    if (data.erp_database_name) fetchSubsidiaries(data.erp_database_name);
  }, [data.erp_database_name]);

  const inputSt = {
    width: '100%', padding: '12px 14px', border: '2px solid rgba(255,255,255,0.15)',
    borderRadius: 8, backgroundColor: '#1a2038', color: '#f9fafb', fontSize: 14,
    outline: 'none', boxSizing: 'border-box',
  };
  const labelSt = { display: 'block', marginBottom: 6, color: '#9ca3af', fontSize: 13, fontWeight: 500 };
  const sectionSt = {
    background: 'rgba(26,32,56,0.8)', padding: 24, borderRadius: 12, marginBottom: 20,
    border: '1px solid rgba(255,255,255,0.1)',
  };

  if (loading) return <Layout><div style={{ padding: 60, textAlign: 'center', color: '#9ca3af' }}>{T('جاري التحميل...', 'Loading...')}</div></Layout>;

  return (
    <Layout>
      <div style={{ direction: isRTL ? 'rtl' : 'ltr', padding: 24, color: '#f9fafb', maxWidth: 900, margin: '0 auto' }}>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700 }}>{T('اعدادات الخدمة الميدانية', 'FSM Settings')}</h1>
            <p style={{ margin: '4px 0 0', color: '#9ca3af', fontSize: 14 }}>{T('اعدادات الربط مع نظام ERP والضرائب', 'ERP integration and tax configuration')}</p>
          </div>
          <button onClick={handleSave} disabled={saving}
            style={{
              padding: '12px 32px', borderRadius: 10, border: 'none', cursor: saving ? 'not-allowed' : 'pointer',
              background: saved ? '#10b981' : '#3b82f6', color: '#fff', fontSize: 15, fontWeight: 700,
              boxShadow: '0 4px 14px rgba(59,130,246,0.3)', transition: 'all 0.3s',
            }}>
            {saving ? T('جاري الحفظ...', 'Saving...') : saved ? T('تم الحفظ', 'Saved!') : T('حفظ الاعدادات', 'Save Settings')}
          </button>
        </div>

        {/* ── ERP Connection ── */}
        <div style={sectionSt}>
          <h3 style={{ margin: '0 0 20px', fontSize: 18, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
            {T('الربط مع نظام MatixERP', 'MatixERP Connection')}
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div>
              <label style={labelSt}>{T('اسم قاعدة بيانات ERP', 'ERP Database Name')} *</label>
              <input type="text" value={data.erp_database_name || ''} onChange={e => handleChange('erp_database_name', e.target.value)}
                placeholder="GL_WEB_COMPANY" style={inputSt} />
              <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>{T('مثال: GL_WEB_SISMATIX', 'Example: GL_WEB_SISMATIX')}</div>
            </div>
            <div>
              <label style={labelSt}>{T('رابط API الوسيط', 'Middleware API URL')}</label>
              <input type="text" value={data.erp_api_url || ''} onChange={e => handleChange('erp_api_url', e.target.value)}
                placeholder="https://erp-api.matix.one" style={inputSt} />
            </div>
          </div>

          <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
            <button onClick={testErpConnection} disabled={testingErp}
              style={{ padding: '10px 20px', borderRadius: 8, border: '2px solid rgba(255,255,255,0.15)', background: 'transparent', color: '#60a5fa', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
              {testingErp ? T('جاري الاختبار...', 'Testing...') : T('اختبار الاتصال', 'Test Connection')}
            </button>
            {erpTestResult && (
              <span style={{ fontSize: 13, fontWeight: 600, color: erpTestResult.ok ? '#4ade80' : '#f87171' }}>
                {erpTestResult.ok ? '\u2705' : '\u274C'} {erpTestResult.msg}
              </span>
            )}
          </div>

          {/* Subsidiary & Branch Selection */}
          {subsidiaries.length > 0 && (
            <div style={{ marginTop: 20, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div>
                <label style={labelSt}>{T('الفرع (Subsidiary)', 'Subsidiary')} *</label>
                <select value={data.erp_subsidiary_id || ''} onChange={e => handleChange('erp_subsidiary_id', e.target.value)} style={inputSt}>
                  <option value="">{T('اختر الفرع...', 'Select subsidiary...')}</option>
                  {subsidiaries.map(s => (
                    <option key={s.Id || s.id} value={s.Id || s.id}>
                      {s.Name || s.name} {s.Code ? '(' + s.Code + ')' : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label style={labelSt}>{T('الشركة / الكيان', 'Company / Entity')}</label>
                <input type="text" value={data.erp_company_name || ''} onChange={e => handleChange('erp_company_name', e.target.value)}
                  placeholder={T('اسم الشركة في ERP', 'Company name in ERP')} style={inputSt} />
              </div>
            </div>
          )}
          {loadingSubs && <div style={{ marginTop: 12, fontSize: 13, color: '#9ca3af' }}>{T('جاري تحميل الفروع...', 'Loading subsidiaries...')}</div>}

          <div style={{ marginTop: 16, display: 'flex', gap: 20 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14, color: '#d1d5db' }}>
              <input type="checkbox" checked={!!data.erp_auto_sync} onChange={e => handleChange('erp_auto_sync', e.target.checked)}
                style={{ width: 18, height: 18, accentColor: '#3b82f6' }} />
              {T('مزامنة تلقائية للعملاء', 'Auto-sync clients')}
            </label>
            {data.erp_auto_sync && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <label style={{ fontSize: 13, color: '#9ca3af' }}>{T('كل', 'Every')}</label>
                <input type="number" value={data.erp_sync_interval_minutes || 60} onChange={e => handleChange('erp_sync_interval_minutes', parseInt(e.target.value))}
                  style={{ ...inputSt, width: 80, textAlign: 'center' }} min={5} />
                <span style={{ fontSize: 13, color: '#9ca3af' }}>{T('دقيقة', 'min')}</span>
              </div>
            )}
          </div>
        </div>

        {/* ── Tax Settings ── */}
        <div style={sectionSt}>
          <h3 style={{ margin: '0 0 20px', fontSize: 18, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
            {T('اعدادات الضريبة', 'Tax Settings')}
          </h3>

          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14, color: '#d1d5db' }}>
              <input type="checkbox" checked={!!data.tax_enabled} onChange={e => handleChange('tax_enabled', e.target.checked)}
                style={{ width: 18, height: 18, accentColor: '#3b82f6' }} />
              {T('تفعيل الضريبة', 'Enable Tax')}
            </label>
          </div>

          {data.tax_enabled && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
                <div>
                  <label style={labelSt}>{T('نوع الضريبة', 'Tax Type')}</label>
                  <select value={data.tax_type || 'vat'} onChange={e => handleChange('tax_type', e.target.value)} style={inputSt}>
                    <option value="vat">{T('ضريبة القيمة المضافة (VAT)', 'Value Added Tax (VAT)')}</option>
                    <option value="sales_tax">{T('ضريبة المبيعات', 'Sales Tax')}</option>
                    <option value="service_tax">{T('ضريبة الخدمات', 'Service Tax')}</option>
                    <option value="custom">{T('مخصص', 'Custom')}</option>
                  </select>
                </div>
                <div>
                  <label style={labelSt}>{T('نسبة الضريبة %', 'Tax Rate %')}</label>
                  <input type="number" value={data.tax_rate || ''} onChange={e => handleChange('tax_rate', parseFloat(e.target.value))}
                    placeholder="15" style={inputSt} min={0} max={100} step={0.01} />
                </div>
                <div>
                  <label style={labelSt}>{T('الرقم الضريبي', 'Tax Registration Number')}</label>
                  <input type="text" value={data.tax_number || ''} onChange={e => handleChange('tax_number', e.target.value)}
                    placeholder="300000000000003" style={inputSt} />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 16 }}>
                <div>
                  <label style={labelSt}>{T('اسم الضريبة (إنجليزي)', 'Tax Name (EN)')}</label>
                  <input type="text" value={data.tax_name || ''} onChange={e => handleChange('tax_name', e.target.value)}
                    placeholder="VAT 15%" style={inputSt} />
                </div>
                <div>
                  <label style={labelSt}>{T('اسم الضريبة (عربي)', 'Tax Name (AR)')}</label>
                  <input type="text" value={data.tax_name_ar || ''} onChange={e => handleChange('tax_name_ar', e.target.value)}
                    placeholder="ضريبة القيمة المضافة 15%" style={{ ...inputSt, direction: 'rtl' }} />
                </div>
              </div>

              <div style={{ marginTop: 16 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14, color: '#d1d5db' }}>
                  <input type="checkbox" checked={!!data.tax_included_in_price} onChange={e => handleChange('tax_included_in_price', e.target.checked)}
                    style={{ width: 18, height: 18, accentColor: '#3b82f6' }} />
                  {T('الضريبة مشمولة في السعر', 'Tax included in price')}
                </label>
                <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4, marginInlineStart: 26 }}>
                  {T('إذا مفعل: السعر المعروض يشمل الضريبة. إذا معطل: الضريبة تضاف فوق السعر', 'If enabled: displayed price includes tax. If disabled: tax is added on top')}
                </div>
              </div>
            </>
          )}
        </div>

        {/* ── Invoice Settings ── */}
        <div style={sectionSt}>
          <h3 style={{ margin: '0 0 20px', fontSize: 18, fontWeight: 700 }}>
            {T('اعدادات الفواتير', 'Invoice Settings')}
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div>
              <label style={labelSt}>{T('العملة', 'Currency')}</label>
              <select value={data.currency || 'SAR'} onChange={e => handleChange('currency', e.target.value)} style={inputSt}>
                <option value="SAR">{T('ريال سعودي (SAR)', 'Saudi Riyal (SAR)')}</option>
                <option value="EGP">{T('جنيه مصري (EGP)', 'Egyptian Pound (EGP)')}</option>
                <option value="KWD">{T('دينار كويتي (KWD)', 'Kuwaiti Dinar (KWD)')}</option>
                <option value="AED">{T('درهم إماراتي (AED)', 'UAE Dirham (AED)')}</option>
                <option value="USD">{T('دولار أمريكي (USD)', 'US Dollar (USD)')}</option>
              </select>
            </div>
            <div>
              <label style={labelSt}>{T('بادئة رقم الفاتورة', 'Invoice Prefix')}</label>
              <input type="text" value={data.invoice_prefix || ''} onChange={e => handleChange('invoice_prefix', e.target.value)}
                placeholder="INV" style={inputSt} />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 16 }}>
            <div>
              <label style={labelSt}>{T('ملاحظات الفاتورة (إنجليزي)', 'Invoice Notes (EN)')}</label>
              <textarea value={data.invoice_notes || ''} onChange={e => handleChange('invoice_notes', e.target.value)}
                style={{ ...inputSt, minHeight: 80, resize: 'vertical' }} placeholder="Payment terms: Net 30 days" />
            </div>
            <div>
              <label style={labelSt}>{T('ملاحظات الفاتورة (عربي)', 'Invoice Notes (AR)')}</label>
              <textarea value={data.invoice_notes_ar || ''} onChange={e => handleChange('invoice_notes_ar', e.target.value)}
                style={{ ...inputSt, minHeight: 80, resize: 'vertical', direction: 'rtl' }} placeholder="شروط الدفع: صافي 30 يوم" />
            </div>
          </div>
        </div>

        {/* Bottom Save */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
          <button onClick={handleSave} disabled={saving}
            style={{
              padding: '14px 40px', borderRadius: 10, border: 'none', cursor: saving ? 'not-allowed' : 'pointer',
              background: saved ? '#10b981' : '#3b82f6', color: '#fff', fontSize: 16, fontWeight: 700,
              boxShadow: '0 4px 14px rgba(59,130,246,0.3)', transition: 'all 0.3s',
            }}>
            {saving ? T('جاري الحفظ...', 'Saving...') : saved ? T('تم الحفظ', 'Saved!') : T('حفظ الاعدادات', 'Save Settings')}
          </button>
        </div>
      </div>
    </Layout>
  );
};

export default FSMSettings;
